import React from 'react';
import {
  SHIPPING_REGIONS,
  SupplierProfile,
  countryName,
  formatCatalogSize,
  formatShippingCost,
  formatShippingWindow,
  formatSyncInterval,
  formatVat,
  hasSupplierTerms,
  shippingRows,
} from '../lib/supplierProfile';

interface Props {
  suppliers: { id: string; name: string | null }[];
  profiles: Record<string, SupplierProfile>;
  connectedIds: Set<string>;
}

const dash = <span className="text-white/20">—</span>;

/**
 * Side-by-side supplier terms: shipping time per region, cost, VAT and how fresh the stock
 * is. Suppliers that have stated something are listed first; the rest stay in the table so
 * a missing answer is visible instead of looking like a missing supplier.
 */
const SupplierComparison: React.FC<Props> = ({ suppliers, profiles, connectedIds }) => {
  const sorted = [...suppliers].sort((a, b) => {
    const diff = Number(hasSupplierTerms(profiles[b.id])) - Number(hasSupplierTerms(profiles[a.id]));
    return diff || (a.name || '').localeCompare(b.name || '');
  });

  return (
    <div className="glass-card rounded-2xl p-5">
      <h3 className="text-base font-heading font-bold text-white mb-1">Compare suppliers</h3>
      <p className="text-white/40 text-xs mb-4">
        Terms are stated by each supplier. Shipping time is in working days, from order to delivery.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm whitespace-nowrap">
          <thead>
            <tr className="text-left text-xs text-white/40">
              <th className="py-2 pr-4 font-medium">Supplier</th>
              {SHIPPING_REGIONS.map((r) => (
                <th key={r.key} className="py-2 pr-4 font-medium">{r.label}</th>
              ))}
              <th className="py-2 pr-4 font-medium">Shipping cost</th>
              <th className="py-2 pr-4 font-medium">Stock refreshed</th>
              <th className="py-2 pr-4 font-medium">VAT</th>
              <th className="py-2 pr-4 font-medium">Ships from</th>
              <th className="py-2 font-medium">Catalogue</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((s) => {
              const p = profiles[s.id];
              const stated = hasSupplierTerms(p);
              const rows = shippingRows(p?.shipping_times);
              return (
                <tr key={s.id} className="border-t border-white/[0.06] text-white/80">
                  {/* Capped so a long supplier name cannot push every term off a phone screen. */}
                  <td className="py-2 pr-4 text-white max-w-[9rem] sm:max-w-[18rem] truncate" title={s.name || undefined}>
                    {s.name || 'Unnamed supplier'}
                    {connectedIds.has(s.id) && <span className="ml-2 text-[11px] text-emerald-400">Connected</span>}
                  </td>
                  {SHIPPING_REGIONS.map((r) => {
                    const row = rows.find((x) => x.key === r.key);
                    // Blank region on a filled-in profile means "does not ship there";
                    // on an empty profile it only means nobody has answered yet.
                    return (
                      <td key={r.key} className="py-2 pr-4">
                        {row
                          ? formatShippingWindow(row.window)
                          : stated ? <span className="text-white/35">Does not ship</span> : dash}
                      </td>
                    );
                  })}
                  <td className="py-2 pr-4">{formatShippingCost(p) ?? dash}</td>
                  <td className="py-2 pr-4">{formatSyncInterval(p?.sync_interval_minutes) ?? dash}</td>
                  <td className="py-2 pr-4">{formatVat(p?.vat_treatment) ?? dash}</td>
                  <td className="py-2 pr-4">{countryName(p?.ships_from_country) ?? dash}</td>
                  <td className="py-2">{formatCatalogSize(p?.max_catalog_size) ?? dash}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SupplierComparison;
